import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AppLayout } from "@/components/layout/AppLayout";
import { AppHeader } from "@/components/layout/AppHeader";
import { QuestionsManagement } from "@/components/admin/QuestionsManagement";
import { ConversationTemplatesManagement } from "@/components/admin/ConversationTemplatesManagement";
import { ConversationStagesManagement } from "@/components/admin/ConversationStagesManagement";
import { ConversationQuestionsManagement } from "@/components/admin/ConversationQuestionsManagement";
import { SkillFrameworksManagement } from "@/components/admin/SkillFrameworksManagement";
import { SurveyTemplatesManagement } from "@/components/admin/SurveyTemplatesManagement";
import { ToolsManagement } from "@/components/admin/ToolsManagement";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import { Footer } from "@/components/layout/Footer";
import * as Sentry from '@sentry/react';

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        return; // Don't set isAdmin or loading when user is null
      }


      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('user_id, is_admin, display_name')
          .eq('user_id', user.id)
          .maybeSingle(); // Use maybeSingle instead of single


        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } else if (profile) {
          setIsAdmin(profile.is_admin || false);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }


  if (!user || isAdmin === false) {
    toast.error(t('admin.accessDeniedDesc'));
    return <Navigate to={`/${(useParams<{ lang?: string }>().lang || 'en')}`} replace />;
  }

  return (
    <AppLayout>
      <AppHeader
        title="Admin Dashboard"
        subtitle="Manage questions, tools, and other system data"
        breadcrumbs={[{ label: "Admin" }]}
      />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Sentry test:</span>
          <ErrorButton />
        </div>
        
        <Tabs defaultValue="questions" className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="questions">Survey Questions</TabsTrigger>
            <TabsTrigger value="survey-templates">Survey Templates</TabsTrigger>
            <TabsTrigger value="conversation-templates">Conversation Templates</TabsTrigger>
            <TabsTrigger value="conversation-stages">Stages</TabsTrigger>
            <TabsTrigger value="conversation-questions">Conv Questions</TabsTrigger>
            <TabsTrigger value="skill-frameworks">Skills</TabsTrigger>
            <TabsTrigger value="tools">Tools</TabsTrigger>
          </TabsList>
          
          <TabsContent value="questions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Survey Questions Management</CardTitle>
                <CardDescription>
                  View and manage all survey questions, their types, and focus areas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <QuestionsManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="survey-templates" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Survey Templates</CardTitle>
                <CardDescription>
                  Manage survey templates for organizing questions into reusable collections
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SurveyTemplatesManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="conversation-templates" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Conversation Templates</CardTitle>
                <CardDescription>
                  Manage conversation templates for performance reviews and development conversations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConversationTemplatesManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="conversation-stages" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Conversation Stages</CardTitle>
                <CardDescription>
                  Manage stages within conversation templates (kickoff, review, evaluation, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConversationStagesManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="conversation-questions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Conversation Questions</CardTitle>
                <CardDescription>
                  Manage questions within conversation stages (subject/manager/both respondents)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConversationQuestionsManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="skill-frameworks" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Skill Frameworks</CardTitle>
                <CardDescription>
                  Manage skill frameworks, categories, and individual skills for performance evaluation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SkillFrameworksManagement />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="tools" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Tools Management</CardTitle>
                <CardDescription>
                  Manage tools, categories, and types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ToolsManagement />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

// Add this button component to test Sentry's error tracking
function ErrorButton() {
  return (
    <button
      className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      onClick={() => {
        Sentry.captureMessage('Manual test: triggering test error from Admin page');
        throw new Error('This is your first error!');
      }}
    >
      Break the world
    </button>
  );
}

export default Admin;